import { type ReactNode } from 'react';
import { useHealth, useList } from '../lib/hooks';
import { fmtTime } from '../lib/util';
import type { AnticrossRow, CacheRow, OePartRow, PartPartRow } from '../lib/types';
import { Chip, DataBlock, Dot, Empty, SourceChip, Table } from '../ui';

function Tile({ num, label, sub }: { num: ReactNode; label: string; sub?: ReactNode }) {
  return (
    <div className="panel p-4">
      <div className="num text-[26px] font-semibold tracking-tight">{num}</div>
      <div className="mt-0.5 text-[11px] uppercase tracking-wider text-muted">{label}</div>
      {sub && <div className="mt-1.5 text-[11.5px] text-faint">{sub}</div>}
    </div>
  );
}

const dash = <span className="text-muted">—</span>;

export default function Dashboard() {
  const { data: health } = useHealth();
  const { data: cache } = useList<CacheRow>('/v1/admin/cache?limit=8');
  const { data: ac } = useList<AnticrossRow>('/v1/admin/anticross?limit=1');
  const { data: oe } = useList<OePartRow>('/v1/admin/xref/oe-part?limit=1');
  const { data: pp } = useList<PartPartRow>('/v1/admin/xref/part-part?limit=1');

  const sources = health?.sources ?? [];
  const curatedTotal =
    ac || oe || pp ? (ac?.total ?? 0) + (oe?.total ?? 0) + (pp?.total ?? 0) : null;
  const recent = cache?.items ?? [];

  return (
    <>
      <div className="mb-3.5 grid gap-3.5" style={{ gridTemplateColumns: 'repeat(4, 1fr)' }}>
        <Tile num={sources.length || dash} label="Sources online" sub={sources.join(' · ') || 'none'} />
        <Tile num={cache ? cache.total.toLocaleString() : dash} label="Cache entries" sub="layer A · 24h TTL" />
        <Tile num={curatedTotal != null ? curatedTotal.toLocaleString() : dash} label="Curated rows" sub="anticross + cross-refs" />
        <Tile num={ac ? ac.total.toLocaleString() : dash} label="Anticross rules" sub="do-not-cross" />
      </div>

      <div className="mb-3.5 grid gap-3.5" style={{ gridTemplateColumns: '1fr 1fr' }}>
        {sources.map((s) => (
          <div key={s} className="blk !mb-0">
            <div className="blk-head">
              <h3 className="text-[13px] font-semibold">
                <SourceChip source={s} />
              </h3>
              <div className="ml-auto">
                <Chip className={health?.status === 'ok' ? 'border-ok text-ok' : 'border-warn text-warn'}>
                  <Dot kind={health?.status === 'ok' ? 'ok' : 'warn'} />
                  {health?.status === 'ok' ? 'operational' : 'degraded'}
                </Chip>
              </div>
            </div>
            <div className="blk-body text-[12px] text-muted">
              Reachable and enabled. Detailed per-source latency and error history arrive with the request log
              (next pass).
            </div>
          </div>
        ))}
      </div>

      <DataBlock title="Recent upstream fetches" sub="latest cached responses (layer A)" raw={cache}>
        {recent.length === 0 ? (
          <Empty>No cached responses yet — run a search.</Empty>
        ) : (
          <Table cols={['Fetched', 'Source', 'Kind', 'Key', 'Status', 'ms']}>
            {recent.map((r) => (
              <tr key={r.id}>
                <td className="mono num">{fmtTime(r.fetchedAt, true)}</td>
                <td>
                  <SourceChip source={r.source} />
                </td>
                <td>{r.queryKind}</td>
                <td className="mono">{r.queryKey}</td>
                <td className={r.httpStatus >= 400 ? 'text-bad' : undefined}>{r.httpStatus}</td>
                <td className="num">{r.latencyMs ?? '—'}</td>
              </tr>
            ))}
          </Table>
        )}
      </DataBlock>
    </>
  );
}
